import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, BookOpen, FileText, GraduationCap, Image as ImageIcon, MapPin, Moon,
    HeartHandshake, Leaf, Phone, Plus, Save, Sparkles, Trash2, Upload, UserRound, Users, X,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import SafeImage from '../../components/SafeImage';
import PrivateDoc from '../../components/PrivateDoc';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { useOptions } from '../../hooks/useOptions';
import { useLocationLists, lookupPin } from '../../utils/locationApi';
import { mediaUrl } from './profileShared';
import { HEIGHT_OPTIONS as HEIGHT_CHOICES, WEIGHT_OPTIONS as WEIGHT_CHOICES } from '../../utils/heights';
import { EDUCATION_OPTIONS } from '../../utils/educationNames';
import { DISABILITY_OPTIONS } from '../../utils/personalInterests';
import { contactTimeText, parseContactTime } from '../../utils/contactTime';
import { RULES, checkFiles, limitNote, uploadFailMessage } from '../../utils/uploadRules';
import './ProfileEdit.css';

// Every field here maps 1:1 to a field on the Profile model (see
// server/models/Profile.js) — adminUpdateProfile saves whatever object it's
// given, so this form covers the member's own info (not partner-preference
// fields, which are a separate, much larger section — add later if needed).
const WORK_SHIFTS = ['Morning shift', 'Day shift (General)', 'Evening shift', 'Night shift', 'Rotational shifts', 'Flexible hours', 'Work from home'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', "Don't know"];

const blankForm = {
    mobile: '', email: '', profileFor: 'Myself',
    name: '', gender: '', dateOfBirth: '', timeOfBirth: '', height: '', weight: '', complexion: '', bloodGroup: '', workShift: '',
    eatingHabits: '', drinkingHabits: '', smokingHabits: '',
    isDeceased: false, numberProtected: false,
    maritalStatus: '', motherTongue: '', knownLanguages: '',
    religion: '', caste: '', subCaste: '', gothra: '',
    rasi: '', nakshatra: '', nakshatraPada: '', dosham: '', doshamType: '',
    education: '', employed: '', occupation: '', occupationRemark: '', annualIncome: '',
    city: '', district: '', state: '', country: '', pincode: '',
    workingCity: '', workingDistrict: '', workingState: '', workingCountry: '', workingPincode: '',
    familyType: '', parentsStatus: '', fatherName: '', fatherOccupation: '', fatherSector: '', motherName: '', motherOccupation: '', motherSector: '',
    siblingList: [],
    altMobile: '', bodyType: '', contactMode: 'any', contactFrom: '', contactTo: '',
    familyStatus: '', familyValues: '', disability: 'None', disabilityDetails: '', hobbies: '', interests: '',
    netWorth: '', assetsText: '', marriageUrgency: '', partnerExpectation: '', prefDistrict: '',
    about: '',
    prefCompleted: false,
    prefAgeMin: '', prefAgeMax: '', prefHeightMin: '', prefHeightMax: '',
    prefMaritalStatus: '', prefMotherTongue: '', prefEatingHabits: '', prefDrinkingHabits: '', prefSmokingHabits: '',
    prefEducation: '', prefOccupation: '', prefAnnualIncome: '',
    prefReligion: '', prefCaste: '', prefSubCaste: '',
    prefRasi: '', prefNakshatra: '', prefDosham: '', prefDoshamType: '',
    prefCountry: '', prefState: '', prefCity: '',
};

const SIBLING_KINDS = [['elderBrother', 'Elder brother'], ['youngerBrother', 'Younger brother'], ['elderSister', 'Elder sister'], ['youngerSister', 'Younger sister']];
const ANY_PREFERENCE = "Any (doesn't matter)";
const HEIGHT_OPTIONS = ["4'6\"", "4'8\"", "4'10\"", "5'0\"", "5'2\"", "5'4\"", "5'6\"", "5'8\"", "5'10\"", "6'0\"", "6'2\""];
const LIFESTYLE_OPTIONS = {
    eating: [ANY_PREFERENCE, 'Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan'],
    drinking: [ANY_PREFERENCE, 'Never', 'Occasionally', 'Regularly'],
    smoking: [ANY_PREFERENCE, 'Never', 'Occasionally', 'Regularly'],
};

const toDateInput = value => (value ? new Date(value).toISOString().slice(0, 10) : '');

const ProfileEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [form, setForm] = useState(blankForm);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingGallery, setUploadingGallery] = useState(false);
    const [uploadingHoroscopeDoc, setUploadingHoroscopeDoc] = useState(false);

    const fetchProfile = useCallback(async () => {
        try {
            const res = await API.get('/admin/profiles');
            const found = (res.data.profiles || []).find(p => p._id === id);
            if (!found) {
                setNotFound(true);
                return;
            }
            setProfile(found);
            setForm({
                mobile: found.user?.mobile || '', email: found.user?.email || '', profileFor: found.profileFor || 'Myself',
                name: found.name || '', gender: found.gender || '', dateOfBirth: toDateInput(found.dateOfBirth), timeOfBirth: found.timeOfBirth || '',
                eatingHabits: found.eatingHabits || '', drinkingHabits: found.drinkingHabits || '', smokingHabits: found.smokingHabits || '',
                isDeceased: Boolean(found.isDeceased), numberProtected: Boolean(found.numberProtected),
                height: found.height || '', weight: found.weight || '', complexion: found.complexion || '', bloodGroup: found.bloodGroup || '', workShift: found.workShift || '',
                maritalStatus: found.maritalStatus || '', motherTongue: found.motherTongue || '',
                knownLanguages: (found.knownLanguages || []).join(', '),
                religion: found.religion || '', caste: found.caste || '', subCaste: found.subCaste || '', gothra: found.gothra || '',
                rasi: found.rasi || '', nakshatra: found.nakshatra || '', nakshatraPada: found.nakshatraPada ? String(found.nakshatraPada) : '', dosham: found.dosham || '', doshamType: found.doshamType || '',
                education: found.education || '', employed: found.employed || '', occupation: found.occupation || '',
                occupationRemark: found.occupationRemark || '', annualIncome: found.annualIncome || '',
                city: found.city || '', district: found.district || '', state: found.state || '', country: found.country || '', pincode: found.pincode || '',
                workingCity: found.workingCity || '', workingDistrict: found.workingDistrict || '',
                workingState: found.workingState || '', workingCountry: found.workingCountry || '', workingPincode: found.workingPincode || '',
                familyType: found.familyType || '', parentsStatus: found.parentsStatus || '', fatherName: found.fatherName || '', fatherOccupation: found.fatherOccupation || '', fatherSector: found.fatherSector || '',
                motherName: found.motherName || '', motherOccupation: found.motherOccupation || '', motherSector: found.motherSector || '',
                // each brother / sister on their own (an older profile with only counts gets one entry per sibling, status not known)
                siblingList: (found.siblingList && found.siblingList.length) ? found.siblingList : SIBLING_KINDS.flatMap(([kind]) => Array.from({ length: Number(found.siblingDetails?.[kind + 's']) || 0 }, () => ({ kind, maritalStatus: '' }))),
                altMobile: found.altMobile || '', bodyType: found.bodyType || '', ...(() => { const c = parseContactTime(found.contactTime); return { contactMode: c.mode, contactFrom: c.from, contactTo: c.to }; })(),
                familyStatus: found.familyStatus || '', familyValues: found.familyValues || '',
                disability: found.disability || 'None', disabilityDetails: found.disabilityDetails || '',
                hobbies: (found.hobbies || []).join(', '), interests: (found.interests || []).join(', '),
                netWorth: found.netWorth || '', assetsText: (found.assets || []).map(a => (a.details ? `${a.type} - ${a.details}` : a.type)).join('\n'),
                marriageUrgency: found.marriageUrgency || '', partnerExpectation: found.partnerExpectation || '', prefDistrict: found.prefDistrict || '',
                about: found.about || '',
                prefCompleted: Boolean(found.prefCompleted),
                prefAgeMin: found.prefAgeMin || '', prefAgeMax: found.prefAgeMax || '',
                prefHeightMin: found.prefHeightMin || '', prefHeightMax: found.prefHeightMax || '',
                prefMaritalStatus: found.prefMaritalStatus || '', prefMotherTongue: found.prefMotherTongue || '',
                prefEatingHabits: found.prefEatingHabits || '', prefDrinkingHabits: found.prefDrinkingHabits || '', prefSmokingHabits: found.prefSmokingHabits || '',
                prefEducation: found.prefEducation || '', prefOccupation: found.prefOccupation || '', prefAnnualIncome: found.prefAnnualIncome || '',
                prefReligion: found.prefReligion || '', prefCaste: found.prefCaste || '', prefSubCaste: found.prefSubCaste || '',
                prefRasi: found.prefRasi || '', prefNakshatra: found.prefNakshatra || '', prefDosham: found.prefDosham || '', prefDoshamType: found.prefDoshamType || '',
                prefCountry: found.prefCountry || '', prefState: found.prefState || '', prefCity: found.prefCity || '',
            });
        } catch (err) {
            toast.error('Failed to load profile');
        }
    }, [id]);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);

    // Live options — so admin can only pick valid taxonomy values instead of
    // free-typing something that won't match member-facing filters (e.g.
    // "Naicker/Vanniya Kula Kshatriyar" typed instead of the real "Vanniyar").
    const { options: religionOptions } = useOptions('religion');
    const { options: casteOptions } = useOptions('caste', { parent: form.religion, allowAllWhenNoParent: true });
    const { options: subCasteOptions } = useOptions('subcaste', {
        parent: form.religion && form.caste ? `${form.religion}|${form.caste}` : '',
        requireParent: true,
    });
    const { countries, states, districts, cities } = useLocationLists(form.country, form.state, form.district);
    const { countries: workingCountries, states: workingStates, districts: workingDistricts, cities: workingCities } = useLocationLists(form.workingCountry, form.workingState, form.workingDistrict);
    const { options: rasiOptions } = useOptions('rasi');
    const { options: nakshatraOptions } = useOptions('nakshatra');
    const { options: doshamTypeOptions } = useOptions('doshamtype');
    const { options: maritalStatusOptions } = useOptions('maritalstatus');
    const { options: jobOptions } = useOptions('job');
    const { options: bodyTypeOptions } = useOptions('bodytype');
    const { options: familyTypeOptions } = useOptions('familytype');
    const { options: familyStatusOptions } = useOptions('familystatus');
    const { options: familyValuesOptions } = useOptions('familyvalues');
    const { options: netWorthOptions } = useOptions('networth');
    const { options: urgencyOptions } = useOptions('marriageurgency');
    const { districts: prefDistrictOptions } = useLocationLists('India', form.prefState && form.prefState !== ANY_PREFERENCE ? form.prefState : '', '');
    const { options: prefCasteOptions } = useOptions('caste', { parent: form.prefReligion, requireParent: true });
    const { options: prefSubCasteOptions } = useOptions('subcaste', {
        parent: form.prefReligion && form.prefCaste ? `${form.prefReligion}|${form.prefCaste}` : '',
        requireParent: true,
    });

    const set = (name, value) => setForm(prev => ({ ...prev, [name]: value }));
    const onChange = e => set(e.target.name, e.target.value);

    // Typing a full 6-digit Indian PIN code fills country, state, district and city (home, or the working place when work = true)
    const [pinNote, setPinNote] = useState({ home: '', work: '' });
    const changePin = (work, rawValue) => {
        const side = work ? 'work' : 'home';
        const field = work ? 'workingPincode' : 'pincode';
        const india = String(work ? form.workingCountry : form.country).trim().toLowerCase() === 'india';
        const pin = india ? rawValue.replace(/\D/g, '').slice(0, 6) : rawValue.slice(0, 12);
        set(field, pin);
        setPinNote(prev => ({ ...prev, [side]: '' }));
        if (!india || pin.length !== 6) return;
        setPinNote(prev => ({ ...prev, [side]: 'Finding the place…' }));
        lookupPin(pin).then(place => {
            if (!place) { setPinNote(prev => ({ ...prev, [side]: 'PIN code not found. Choose the place yourself.' })); return; }
            const district = place.districtOther || place.district;
            setForm(prev => {
                if (prev[field] !== pin) return prev; // the PIN was changed again while this was loading
                return work
                    ? { ...prev, workingCountry: place.country, workingState: place.state, workingDistrict: district, workingCity: place.city }
                    : { ...prev, country: place.country, state: place.state, district, city: place.city };
            });
            setPinNote(prev => ({ ...prev, [side]: `Filled from PIN code: ${[place.city, district, place.state].filter(Boolean).join(', ')}` }));
        });
    };

    const handleSave = async e => {
        e.preventDefault();
        if (!/^[0-9]{10}$/.test(form.mobile)) { toast.error('Enter a valid 10-digit mobile number'); return; }
        if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) { toast.error('Enter a valid email address, or leave it empty'); return; }
        if (form.contactMode === 'specific' && !contactTimeText('specific', form.contactFrom, form.contactTo)) { toast.error(form.contactFrom && form.contactFrom === form.contactTo ? 'Contact start and end time cannot be the same' : 'Choose both the contact start time and end time'); return; }
        if (form.timeOfBirth && !/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(form.timeOfBirth)) { toast.error('Time of birth must look like 06:30 (24-hour)'); return; }
        setSaving(true);
        try {
            // the account's mobile number and email belong to the login account, not the profile: saved first, so a refused value stops here
            const userId = profile?.user?._id;
            if (userId && (form.mobile !== (profile.user.mobile || '') || form.email.trim().toLowerCase() !== String(profile.user.email || '').toLowerCase())) {
                await API.put(`/admin/users/${userId}/contact`, { mobile: form.mobile, email: form.email });
            }
            const { mobile, email, nakshatraPada, contactMode, contactFrom, contactTo, assetsText, hobbies, interests, ...profileFields } = form;
            const payload = {
                ...profileFields,
                nakshatraPada: nakshatraPada ? Number(nakshatraPada) : null,
                knownLanguages: form.knownLanguages.split(',').map(s => s.trim()).filter(Boolean),
                hobbies: hobbies.split(',').map(v => v.trim()).filter(Boolean),
                interests: interests.split(',').map(v => v.trim()).filter(Boolean),
                // one asset per line: "Type - details"
                assets: assetsText.split('\n').map(line => line.trim()).filter(Boolean).map(line => {
                    const cut = line.indexOf(' - ');
                    return cut < 0 ? { type: line, details: '' } : { type: line.slice(0, cut).trim(), details: line.slice(cut + 3).trim() };
                }),
                contactTime: contactTimeText(contactMode, contactFrom, contactTo),
                doshamType: form.dosham === 'Yes' ? form.doshamType : '',
                prefDoshamType: form.prefDosham === 'Yes' ? form.prefDoshamType : '',
                prefCompleted: form.prefCompleted || Boolean([
                    form.prefAgeMin, form.prefAgeMax, form.prefReligion, form.prefCaste,
                    form.prefOccupation, form.prefState, form.prefRasi,
                ].some(Boolean)),
            };
            await API.put(`/admin/profiles/${id}`, payload);
            toast.success('Profile updated!');
            navigate(`/profiles/${id}`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    // Profile photo / gallery act immediately (not bundled into the text-field
    // Save button) — same pattern as the Aadhar document uploads on the View
    // page, since these are separate multipart endpoints, not part of `form`.
    const handleUploadPhoto = async file => {
        if (!file) return;
        if (!checkFiles([file], RULES.photo).length) return;
        setUploadingPhoto(true);
        const formData = new FormData();
        formData.append('photo', file);
        try {
            const res = await API.post(`/admin/profiles/${id}/photo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Profile photo updated!');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error(uploadFailMessage(err, [file], RULES.photo));
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleRemovePhoto = async () => {
        try {
            const res = await API.delete(`/admin/profiles/${id}/photo`);
            toast.success('Profile photo removed');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error('Failed to remove photo');
        }
    };

    const handleUploadGallery = async files => {
        if (!Array.from(files || []).length) return;
        const fileList = checkFiles(files, RULES.photo, { have: (profile.photos || []).length, maxFiles: 15 });
        if (!fileList.length) return;
        setUploadingGallery(true);
        const formData = new FormData();
        fileList.forEach(f => formData.append('photos', f));
        try {
            const res = await API.post(`/admin/profiles/${id}/photos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success(res.data.message || 'Photo(s) uploaded!');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error(uploadFailMessage(err, fileList, RULES.photo));
        } finally {
            setUploadingGallery(false);
        }
    };

    const handleRemoveGalleryPhoto = async photoUrl => {
        try {
            const filename = photoUrl.split('/').pop();
            const res = await API.delete(`/admin/profiles/${id}/photos/${filename}`);
            toast.success('Photo removed');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error('Failed to remove photo');
        }
    };

    const handleUploadHoroscopeDoc = async files => {
        if (!Array.from(files || []).length) return;
        const fileList = checkFiles(files, RULES.document, { have: profile.horoscopeDocuments?.length || 0, maxFiles: 2 });
        if (!fileList.length) return;
        setUploadingHoroscopeDoc(true);
        const formData = new FormData();
        fileList.forEach(file => formData.append('documents', file));
        try {
            const res = await API.post(`/admin/profiles/${id}/horoscope-doc`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success(res.data.message || 'Horoscope document(s) uploaded');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error(uploadFailMessage(err, fileList, RULES.document));
        } finally {
            setUploadingHoroscopeDoc(false);
        }
    };

    const handleRemoveHoroscopeDoc = async documentUrl => {
        try {
            const filename = documentUrl.split('/').pop();
            const res = await API.delete(`/admin/profiles/${id}/horoscope-doc/${encodeURIComponent(filename)}`);
            toast.success(res.data.message || 'Horoscope document removed');
            setProfile(res.data.profile);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to remove horoscope document');
        }
    };

    if (notFound) {
        return (
            <div className="profile-edit-layout">
                <Sidebar />
                <div className="admin-content profile-edit-content">
                    <Navbar title={<span className="profile-edit-nav-title"><UserRound size={18} /> Edit profile</span>} />
                    <main className="profile-edit-page">
                        <button type="button" className="profile-edit-back" onClick={() => navigate('/profiles')}>
                            <ArrowLeft size={15} /> Back to Profiles
                        </button>
                        <section className="profile-edit-not-found"><span><UserRound size={30} /></span><h1>Profile not found</h1><p>This profile may have been deleted or is no longer available.</p></section>
                    </main>
                </div>
            </div>
        );
    }

    // While loading, the global honeycomb overlay already covers the wait.
    if (!profile) {
        return (
            <div className="profile-edit-layout">
                <Sidebar />
                <div className="admin-content profile-edit-content">
                    <Navbar title={<span className="profile-edit-nav-title"><UserRound size={18} /> Edit profile</span>} />
                </div>
            </div>
        );
    }

    return (
        <div className="profile-edit-layout">
            <Sidebar />
            <div className="admin-content profile-edit-content">
                <Navbar title={<span className="profile-edit-nav-title"><UserRound size={18} /> Edit profile</span>} />
                <form className="profile-edit-page" onSubmit={handleSave}>
                    <div className="profile-edit-topbar">
                        <button type="button" className="profile-edit-back" onClick={() => navigate(`/profiles/${id}`)}><ArrowLeft size={15} /> Back to profile</button>
                        <span>Member ID: {profile.memberId || profile.user?.memberId || 'Pending assignment'}</span>
                    </div>

                    <header className="profile-edit-hero">
                        <div className="profile-edit-hero__icon"><Sparkles size={23} /></div>
                        <div><span>Profile management</span><h1>Edit {profile.name || profile.user?.name || 'member'}'s profile</h1><p>Keep personal, community and match information accurate and up to date.</p></div>
                        <button type="submit" disabled={saving}><Save size={16} /> {saving ? 'Saving…' : 'Save changes'}</button>
                    </header>

                    <div className="profile-edit-grid">
                        <section className="profile-edit-section profile-edit-media">
                            <SectionHeader icon={ImageIcon} title="Profile photos" subtitle="Manage the primary image and gallery" />
                            <div className="profile-edit-media__body">
                                <div className="profile-edit-primary-photo">
                                    <span className="profile-edit-media-label">Primary photo</span>
                                    <small className="upload-limit"><b>Limit:</b> {limitNote(RULES.photo)}</small>
                                    <SafeImage
                                        src={profile.photo ? mediaUrl(profile.photo) : ''}
                                        alt="Profile photo"
                                        className="profile-edit-primary-photo__image"
                                    />
                                    <div className="profile-edit-primary-photo__actions">
                                        <label className={`profile-edit-upload-btn ${uploadingPhoto ? 'is-loading' : ''}`}>
                                            {uploadingPhoto
                                                ? 'Uploading…'
                                                : <><Upload size={13} /> {profile.photo ? 'Replace' : 'Upload'}</>}
                                            <input type="file" accept="image/*" disabled={uploadingPhoto}
                                                onChange={e => { handleUploadPhoto(e.target.files[0]); e.target.value = ''; }} />
                                        </label>
                                        {profile.photo && (
                                            <button type="button" className="profile-edit-remove-btn" onClick={handleRemovePhoto}>
                                                <Trash2 size={13} /> Remove
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="profile-edit-gallery">
                                    <div className="profile-edit-media-label">Gallery <span>{(profile.photos || []).length}/15</span></div>
                                    <small className="upload-limit"><b>Limit:</b> {limitNote(RULES.photo, { maxFiles: 15 })}</small>
                                    <div className="profile-edit-gallery__grid">
                                        {(profile.photos || []).map((p, i) => (
                                            <div className="profile-edit-gallery__item" key={p}>
                                                <SafeImage src={mediaUrl(p)} alt={`Gallery photo ${i + 1}`} />
                                                <button type="button"
                                                    onClick={() => handleRemoveGalleryPhoto(p)} aria-label="Remove photo">
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}
                                        {(profile.photos || []).length < 15 && (
                                            <label className={`profile-edit-gallery__add ${uploadingGallery ? 'is-loading' : ''}`}>
                                                {uploadingGallery ? <span>…</span> : <><Plus size={20} /><small>Add photos</small></>}
                                                <input type="file" accept="image/*" multiple disabled={uploadingGallery}
                                                    onChange={e => { handleUploadGallery(e.target.files); e.target.value = ''; }} />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="profile-edit-section profile-edit-section--wide profile-edit-documents">
                            <SectionHeader icon={FileText} title="Horoscope documents" subtitle="Upload the horoscope as a photo or PDF file" />
                            <div className="profile-edit-documents__body">
                                <div className="profile-edit-document-slots">
                                    {[0].map(index => {
                                        const document = profile.horoscopeDocuments?.[index];
                                        const side = 'Horoscope';
                                        return (
                                            <div className={`profile-edit-document-slot ${document ? 'has-document' : 'is-empty'}`} key={side}>
                                                <span className="profile-edit-document-slot__label">{side}</span>
                                                {document ? (
                                                    <>
                                                        <PrivateDoc url={document} alt={`Horoscope ${side.toLowerCase()}`} pdfClassName="profile-edit-document-pdf" pdfLabel={`${side} PDF`} />
                                                        <button type="button" className="profile-edit-document-remove" onClick={() => handleRemoveHoroscopeDoc(document)} aria-label={`Remove horoscope ${side.toLowerCase()}`}>
                                                            <Trash2 size={13} /> Remove
                                                        </button>
                                                    </>
                                                ) : (
                                                    <div className="profile-edit-document-empty"><FileText size={24} /><span>No {side.toLowerCase()} document</span></div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {(profile.horoscopeDocuments?.length || 0) < 2 && (
                                    <label className={`profile-edit-document-upload ${uploadingHoroscopeDoc ? 'is-loading' : ''}`}>
                                        <Upload size={15} />
                                        {uploadingHoroscopeDoc
                                            ? 'Uploading…'
                                            : (profile.horoscopeDocuments?.length || 0) === 0
                                                ? 'Upload front and back'
                                                : 'Upload back document'}
                                        <input type="file" accept="image/*,.pdf,application/pdf" multiple disabled={uploadingHoroscopeDoc}
                                            onChange={event => { handleUploadHoroscopeDoc(event.target.files); event.target.value = ''; }} />
                                    </label>
                                )}
                                <p className="profile-edit-document-help"><b>Limit:</b> {limitNote(RULES.document, { maxFiles: 2 })}. Upload front first, then back.</p>
                            </div>
                        </section>

                        <Section icon={Phone} title="Account & contact" subtitle="Login mobile number and email (changing one makes it unverified until a code is verified)">
                            <Field label="Mobile Number">
                                <input name="mobile" value={form.mobile} inputMode="numeric" maxLength={10} placeholder="10-digit mobile number"
                                    onChange={e => set('mobile', e.target.value.replace(/[^0-9]/g, '').slice(0, 10))} />
                            </Field>
                            <Field label="Email">
                                <input name="email" type="email" value={form.email} onChange={onChange} placeholder="name@example.com" />
                            </Field>
                            <Field label="Profile created for">
                                <Select ariaLabel="Profile created for" value={form.profileFor} allowEmpty={false} onChange={v => set('profileFor', v)}
                                    options={['Myself', 'Son', 'Daughter', 'Brother', 'Sister', 'Relative', 'Friend']} />
                            </Field>
                        </Section>

                        <Section icon={UserRound} title="Personal details" subtitle="Basic information shown on the member profile">
                            <Field label="Name"><input name="name" value={form.name} onChange={onChange} /></Field>
                            <Field label="Alternative Mobile" hint="10 digits"><input name="altMobile" inputMode="numeric" maxLength={10} value={form.altMobile} onChange={e => set('altMobile', e.target.value.replace(/\D/g, ''))} /></Field>
                            <Field label="Gender">
                                <Select value={form.gender} onChange={v => set('gender', v)} options={['Male', 'Female']} ariaLabel="Gender" />
                            </Field>
                            <Field label="Date of Birth">
                                <DatePicker value={form.dateOfBirth} onChange={v => set('dateOfBirth', v)} />
                            </Field>
                            <Field label="Time of Birth" hint="24-hour, like 06:30">
                                <input name="timeOfBirth" value={form.timeOfBirth} onChange={e => set('timeOfBirth', e.target.value.replace(/[^0-9:]/g, '').slice(0, 5))} placeholder="HH:MM" maxLength={5} />
                            </Field>
                            <Field label="Height" hint="saved like 5'6&quot;">
                                <Select ariaLabel="Height" value={form.height} onChange={v => set('height', v)} options={[...HEIGHT_CHOICES, ...(form.height && !HEIGHT_CHOICES.some(h => h.value === form.height) ? [form.height] : [])]} />
                            </Field>
                            <Field label="Weight">
                                <Select ariaLabel="Weight" value={form.weight} onChange={v => set('weight', v)} options={[...WEIGHT_CHOICES, ...(form.weight && !WEIGHT_CHOICES.some(w => w.value === form.weight) ? [form.weight] : [])]} />
                            </Field>
                            <Field label="Body Type">
                                <Select ariaLabel="Body type" value={form.bodyType} onChange={v => set('bodyType', v)} options={[...bodyTypeOptions, ...(form.bodyType && !bodyTypeOptions.includes(form.bodyType) ? [form.bodyType] : [])]} />
                            </Field>
                            <Field label="Contact Time">
                                <Select ariaLabel="Contact time" allowEmpty={false} value={form.contactMode} onChange={v => set('contactMode', v)} options={[{ value: 'any', label: 'Any time' }, { value: 'specific', label: 'Specific time' }]} />
                            </Field>
                            {form.contactMode === 'specific' && (
                                <>
                                    <Field label="Contact from"><input type="time" value={form.contactFrom} onChange={e => set('contactFrom', e.target.value)} /></Field>
                                    <Field label="Contact until"><input type="time" value={form.contactTo} onChange={e => set('contactTo', e.target.value)} /></Field>
                                </>
                            )}
                            <Field label="Complexion"><input name="complexion" value={form.complexion} onChange={onChange} /></Field>
                            <Field label="Blood Group">
                                <Select ariaLabel="Blood group" value={form.bloodGroup} onChange={v => set('bloodGroup', v)} options={BLOOD_GROUPS} />
                            </Field>
                            <Field label="Marital Status">
                                <Select ariaLabel="Marital status" value={form.maritalStatus} onChange={v => set('maritalStatus', v)} options={[
                                    'Never Married', 'Unmarried', 'Divorced', 'Widowed', 'Awaiting divorce', 'Separated',
                                ]} />
                            </Field>
                            <Field label="Eating Habits"><Select ariaLabel="Eating habits" value={form.eatingHabits} onChange={v => set('eatingHabits', v)} options={['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan']} /></Field>
                            <Field label="Drinking Habits"><Select ariaLabel="Drinking habits" value={form.drinkingHabits} onChange={v => set('drinkingHabits', v)} options={['Never', 'Occasionally', 'Socially', 'Regularly']} /></Field>
                            <Field label="Smoking Habits"><Select ariaLabel="Smoking habits" value={form.smokingHabits} onChange={v => set('smokingHabits', v)} options={['Never', 'Occasionally', 'Socially', 'Regularly']} /></Field>
                            <Field label="Mother Tongue"><input name="motherTongue" value={form.motherTongue} onChange={onChange} /></Field>
                            <Field label="Known Languages" hint="comma-separated">
                                <input name="knownLanguages" value={form.knownLanguages} onChange={onChange} placeholder="Tamil, English" />
                            </Field>
                        </Section>

                        <Section icon={BookOpen} title="Religion & community" subtitle="Community information used for profile discovery">
                            <Field label="Religion">
                                <Select ariaLabel="Religion" value={form.religion} options={religionOptions}
                                    onChange={v => setForm(prev => ({ ...prev, religion: v, caste: '', subCaste: '' }))} />
                            </Field>
                            <Field label="Caste">
                                <Select ariaLabel="Caste" value={form.caste} options={casteOptions}
                                    onChange={v => setForm(prev => ({ ...prev, caste: v, subCaste: '' }))} />
                            </Field>
                            <Field label="Sub Caste">
                                <Select ariaLabel="Sub caste" value={form.subCaste} onChange={v => set('subCaste', v)} options={subCasteOptions} />
                            </Field>
                            <Field label="Gothra"><input name="gothra" value={form.gothra} onChange={onChange} /></Field>
                        </Section>

                        <Section icon={Moon} title="Horoscope" subtitle="Astrological information for compatibility matching">
                            <Field label="Rasi">
                                <Select ariaLabel="Rasi" value={form.rasi} onChange={v => set('rasi', v)} options={rasiOptions} />
                            </Field>
                            <Field label="Nakshatra">
                                <Select ariaLabel="Nakshatra" value={form.nakshatra} onChange={v => set('nakshatra', v)} options={nakshatraOptions} />
                            </Field>
                            <Field label="Nakshatra Pada">
                                <Select ariaLabel="Nakshatra pada" value={form.nakshatraPada} onChange={v => set('nakshatraPada', v)} options={['1', '2', '3', '4']} />
                            </Field>
                            <Field label="Dosham">
                                <Select ariaLabel="Dosham" value={form.dosham}
                                    onChange={v => setForm(prev => ({ ...prev, dosham: v, doshamType: v === 'Yes' ? prev.doshamType : '' }))}
                                    options={['No', 'Yes', "Don't Know"]} />
                            </Field>
                            {form.dosham === 'Yes' && (
                                <Field label="Dosham Type">
                                    <Select ariaLabel="Dosham type" value={form.doshamType} onChange={v => set('doshamType', v)}
                                        options={doshamTypeOptions} placeholder="Select Dosham type" />
                                </Field>
                            )}
                        </Section>

                        <Section icon={GraduationCap} title="Education & career" subtitle="Qualifications, employment and income">
                            <Field label="Education">
                                <Select ariaLabel="Education" value={form.education} onChange={v => set('education', v)} options={[...EDUCATION_OPTIONS, ...(form.education && !EDUCATION_OPTIONS.some(o => o.value === form.education) ? [form.education] : [])]} />
                            </Field>
                            <Field label="Employed"><input name="employed" value={form.employed} onChange={onChange} /></Field>
                            <Field label="Occupation">
                                <Select ariaLabel="Occupation" value={form.occupation} onChange={v => set('occupation', v)} options={jobOptions} />
                            </Field>
                            <Field label="Work Schedule (Shift)">
                                <Select ariaLabel="Work shift" value={form.workShift} onChange={v => set('workShift', v)} options={WORK_SHIFTS} />
                            </Field>
                            <Field label="Occupation Remark"><input name="occupationRemark" value={form.occupationRemark} onChange={onChange} /></Field>
                            <Field label="Annual Income"><input name="annualIncome" value={form.annualIncome} onChange={onChange} /></Field>
                        </Section>

                        <Section icon={MapPin} title="Location" subtitle="Home and working locations">
                            <Field label="City"><Select ariaLabel="City" value={form.city} onChange={v => set('city', v)} options={[...new Set([form.city, ...cities].filter(Boolean))]} disabled={!form.state} placeholder={form.state ? 'Select city' : 'Select state first'} /></Field>
                                <Field label="State">
                                <Select ariaLabel="State" value={form.state} options={states}
                                    onChange={v => setForm(prev => ({ ...prev, state: v, district: '', city: '' }))} />
                                </Field>
                                <Field label="District">
                                <Select ariaLabel="District" value={form.district} onChange={v => setForm(prev => ({ ...prev, district: v, city: '' }))} options={districts}
                                    disabled={!form.state} placeholder={form.state ? 'Select' : 'Select state first'} />
                                </Field>
                            <Field label="Country"><Select ariaLabel="Country" value={form.country} onChange={v => setForm(prev => ({ ...prev, country: v, state: '', district: '', city: '' }))} options={countries} /></Field>
                            <Field label={String(form.country).trim().toLowerCase() === 'india' ? 'PIN Code' : 'Postal Code'}>
                                <input name="pincode" value={form.pincode} onChange={e => changePin(false, e.target.value)} inputMode="numeric" maxLength={12}
                                    placeholder={String(form.country).trim().toLowerCase() === 'india' ? '6-digit PIN code' : 'Postal code'} />
                                {pinNote.home && <small style={{ display: 'block', marginTop: '5px', color: 'var(--admin-text-muted)' }}>{pinNote.home}</small>}
                            </Field>
                            <div aria-hidden="true" className="profile-edit-grid-gap" />
                            <Field label="Working City"><Select ariaLabel="Working city" value={form.workingCity} onChange={v => set('workingCity', v)} options={[...new Set([form.workingCity, ...workingCities].filter(Boolean))]} disabled={!form.workingState} placeholder={form.workingState ? 'Select city' : 'Select state first'} /></Field>
                            <Field label="Working District"><Select ariaLabel="Working district" value={form.workingDistrict} onChange={v => setForm(prev => ({ ...prev, workingDistrict: v, workingCity: '' }))} options={workingDistricts} disabled={!form.workingState} placeholder={form.workingState ? 'Select district' : 'Select state first'} /></Field>
                            <Field label="Working State"><Select ariaLabel="Working state" value={form.workingState} onChange={v => setForm(prev => ({ ...prev, workingState: v, workingDistrict: '', workingCity: '' }))} options={workingStates} disabled={!form.workingCountry} placeholder={form.workingCountry ? 'Select state' : 'Select country first'} /></Field>
                            <Field label="Working Country"><Select ariaLabel="Working country" value={form.workingCountry} onChange={v => setForm(prev => ({ ...prev, workingCountry: v, workingState: '', workingDistrict: '', workingCity: '' }))} options={workingCountries} /></Field>
                            <Field label={String(form.workingCountry).trim().toLowerCase() === 'india' ? 'Working PIN Code' : 'Working Postal Code'}>
                                <input name="workingPincode" value={form.workingPincode} onChange={e => changePin(true, e.target.value)} inputMode="numeric" maxLength={12}
                                    placeholder={String(form.workingCountry).trim().toLowerCase() === 'india' ? '6-digit PIN code' : 'Postal code'} />
                                {pinNote.work && <small style={{ display: 'block', marginTop: '5px', color: 'var(--admin-text-muted)' }}>{pinNote.work}</small>}
                            </Field>
                        </Section>

                        <Section icon={Leaf} title="Health, hobbies & interests" subtitle="Disability, hobbies and interests the member shared">
                            <Field label="Any Disability?">
                                <Select ariaLabel="Disability" allowEmpty={false} value={form.disability} onChange={v => setForm(prev => ({ ...prev, disability: v, disabilityDetails: v === 'None' ? '' : prev.disabilityDetails }))} options={DISABILITY_OPTIONS} />
                            </Field>
                            {form.disability !== 'None' && <Field label="Disability details"><input name="disabilityDetails" maxLength={200} value={form.disabilityDetails} onChange={onChange} /></Field>}
                            <Field label="Hobbies" hint="comma-separated"><input name="hobbies" value={form.hobbies} onChange={onChange} placeholder="Reading, Music, Cooking" /></Field>
                            <Field label="Interests" hint="comma-separated"><input name="interests" value={form.interests} onChange={onChange} placeholder="Technology, Travel" /></Field>
                        </Section>

                        <Section icon={Sparkles} title="Assets & net worth" subtitle="What the member owns">
                            <Field label="Net Worth">
                                <Select ariaLabel="Net worth" value={form.netWorth} onChange={v => set('netWorth', v)} options={[...netWorthOptions, ...(form.netWorth && !netWorthOptions.includes(form.netWorth) ? [form.netWorth] : [])]} />
                            </Field>
                            <Field label="Assets" hint="one per line, like: Own House / Flat - 2 BHK in Chennai">
                                <textarea name="assetsText" rows={4} value={form.assetsText} onChange={onChange} />
                            </Field>
                        </Section>

                        <Section icon={Users} title="Family details" subtitle="Household and immediate family information">
                            <Field label="Family Type">
                                <Select ariaLabel="Family type" value={form.familyType} onChange={v => set('familyType', v)} options={[...(familyTypeOptions.length ? familyTypeOptions : ['Nuclear', 'Joint']), ...(form.familyType && !familyTypeOptions.includes(form.familyType) && !['Nuclear', 'Joint'].includes(form.familyType) ? [form.familyType] : [])]} />
                            </Field>
                            <Field label="Family Status">
                                <Select ariaLabel="Family status" value={form.familyStatus} onChange={v => set('familyStatus', v)} options={[...familyStatusOptions, ...(form.familyStatus && !familyStatusOptions.includes(form.familyStatus) ? [form.familyStatus] : [])]} />
                            </Field>
                            <Field label="Family Values">
                                <Select ariaLabel="Family values" value={form.familyValues} onChange={v => set('familyValues', v)} options={[...familyValuesOptions, ...(form.familyValues && !familyValuesOptions.includes(form.familyValues) ? [form.familyValues] : [])]} />
                            </Field>
                            <Field label="Parents' Status"><Select ariaLabel="Parents status" value={form.parentsStatus} onChange={v => set('parentsStatus', v)} options={['Both Parents Living', 'Semi-Orphan (Father Deceased)', 'Semi-Orphan (Mother Deceased)', 'Orphan (Both Parents Deceased)']} /></Field>
                            <Field label="Father's Name"><input name="fatherName" value={form.fatherName} onChange={onChange} /></Field>
                            <Field label="Father's Occupation"><input name="fatherOccupation" value={form.fatherOccupation} onChange={onChange} /></Field>
                            <Field label="Father's Sector"><input name="fatherSector" value={form.fatherSector} onChange={onChange} placeholder="Government, Private, Business..." /></Field>
                            <Field label="Mother's Name"><input name="motherName" value={form.motherName} onChange={onChange} /></Field>
                            <Field label="Mother's Occupation"><input name="motherOccupation" value={form.motherOccupation} onChange={onChange} /></Field>
                            <Field label="Mother's Sector"><input name="motherSector" value={form.motherSector} onChange={onChange} placeholder="Government, Private, Business..." /></Field>
                            {SIBLING_KINDS.map(([kind, label]) => {
                                const group = form.siblingList.filter(item => item.kind === kind);
                                const rebuild = nextGroup => set('siblingList', SIBLING_KINDS.flatMap(([k]) => (k === kind ? nextGroup : form.siblingList.filter(item => item.kind === k))));
                                return (
                                    <React.Fragment key={kind}>
                                        <Field label={`${label}s`}>
                                            <Select ariaLabel={`${label}s`} value={String(group.length)} allowEmpty={false}
                                                onChange={v => { const n = Number(v); rebuild(Array.from({ length: n }, (_, i) => group[i] || { kind, maritalStatus: '' })); }}
                                                options={Array.from({ length: 11 }, (_, i) => String(i))} />
                                        </Field>
                                        {group.map((item, index) => (
                                            <Field key={index} label={`${label} ${index + 1}: married?`}>
                                                <Select ariaLabel={`${label} ${index + 1} marital status`} value={item.maritalStatus || ''} placeholder="Not set"
                                                    onChange={v => rebuild(group.map((x, i) => (i === index ? { ...x, maritalStatus: v } : x)))} options={['Married', 'Unmarried']} />
                                            </Field>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </Section>

                        <Section icon={FileText} title="Status & privacy" subtitle="How this profile is treated on the site">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', gridColumn: '1 / -1' }}>
                                <input type="checkbox" checked={Boolean(form.numberProtected)} onChange={e => set('numberProtected', e.target.checked)} />
                                Protect the mobile number (others must ask before they can see it)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', gridColumn: '1 / -1' }}>
                                <input type="checkbox" checked={Boolean(form.isDeceased)} onChange={e => set('isDeceased', e.target.checked)} />
                                The person has passed away (the profile shows "In Remembrance")
                            </label>
                        </Section>

                        <section className="profile-edit-section profile-edit-section--wide">
                            <SectionHeader icon={HeartHandshake} title="Partner preferences" subtitle="The qualities and background this member is looking for" />
                            <div className="profile-edit-fields profile-edit-fields--preferences">
                                <Field label="Minimum Age"><input type="number" min="18" max="80" name="prefAgeMin" value={form.prefAgeMin} onChange={onChange} /></Field>
                                <Field label="Maximum Age"><input type="number" min="18" max="80" name="prefAgeMax" value={form.prefAgeMax} onChange={onChange} /></Field>
                                <Field label="Minimum Height"><Select ariaLabel="Preferred minimum height" value={form.prefHeightMin} onChange={v => set('prefHeightMin', v)} options={[ANY_PREFERENCE, ...HEIGHT_OPTIONS]} /></Field>
                                <Field label="Maximum Height"><Select ariaLabel="Preferred maximum height" value={form.prefHeightMax} onChange={v => set('prefHeightMax', v)} options={[ANY_PREFERENCE, ...HEIGHT_OPTIONS]} /></Field>
                                <Field label="Marital Status"><Select ariaLabel="Preferred marital status" value={form.prefMaritalStatus} onChange={v => set('prefMaritalStatus', v)} options={[ANY_PREFERENCE, ...(maritalStatusOptions.length ? maritalStatusOptions : ['Never Married', 'Widowed', 'Divorced', 'Separated'])]} /></Field>
                                <Field label="Mother Tongue"><input name="prefMotherTongue" value={form.prefMotherTongue} onChange={onChange} placeholder={ANY_PREFERENCE} /></Field>
                                <Field label="Religion">
                                    <Select ariaLabel="Preferred religion" value={form.prefReligion} options={[ANY_PREFERENCE, ...religionOptions]}
                                        onChange={v => setForm(prev => ({ ...prev, prefReligion: v, prefCaste: '', prefSubCaste: '' }))} />
                                </Field>
                                <Field label="Caste">
                                    <Select ariaLabel="Preferred caste" value={form.prefCaste} options={[ANY_PREFERENCE, ...prefCasteOptions]}
                                        disabled={!form.prefReligion || form.prefReligion === ANY_PREFERENCE}
                                        onChange={v => setForm(prev => ({ ...prev, prefCaste: v, prefSubCaste: '' }))} />
                                </Field>
                                <Field label="Sub Caste">
                                    <Select ariaLabel="Preferred sub caste" value={form.prefSubCaste} options={[ANY_PREFERENCE, ...prefSubCasteOptions]}
                                        disabled={!form.prefCaste || form.prefCaste === ANY_PREFERENCE} onChange={v => set('prefSubCaste', v)} />
                                </Field>
                                <Field label="Education"><input name="prefEducation" value={form.prefEducation} onChange={onChange} placeholder={ANY_PREFERENCE} /></Field>
                                <Field label="Occupation"><Select ariaLabel="Preferred occupation" value={form.prefOccupation} onChange={v => set('prefOccupation', v)} options={[ANY_PREFERENCE, ...jobOptions]} /></Field>
                                <Field label="Annual Income"><input name="prefAnnualIncome" value={form.prefAnnualIncome} onChange={onChange} placeholder={ANY_PREFERENCE} /></Field>
                                <Field label="Country"><input name="prefCountry" value={form.prefCountry} onChange={onChange} placeholder={ANY_PREFERENCE} /></Field>
                                <Field label="State"><Select ariaLabel="Preferred state" value={form.prefState} onChange={v => set('prefState', v)} options={[ANY_PREFERENCE, ...states]} /></Field>
                                <Field label="District"><Select ariaLabel="Preferred district" value={form.prefDistrict} disabled={!form.prefState || form.prefState === ANY_PREFERENCE} onChange={v => set('prefDistrict', v)} options={[ANY_PREFERENCE, ...prefDistrictOptions]} /></Field>
                                <Field label="City"><input name="prefCity" value={form.prefCity} onChange={onChange} placeholder={ANY_PREFERENCE} /></Field>
                                <Field label="Rasi"><Select ariaLabel="Preferred Rasi" value={form.prefRasi} onChange={v => set('prefRasi', v)} options={[ANY_PREFERENCE, ...rasiOptions]} /></Field>
                                <Field label="Nakshatra"><Select ariaLabel="Preferred Nakshatra" value={form.prefNakshatra} onChange={v => set('prefNakshatra', v)} options={[ANY_PREFERENCE, ...nakshatraOptions]} /></Field>
                                <Field label="Dosham">
                                    <Select ariaLabel="Preferred Dosham" value={form.prefDosham}
                                        onChange={v => setForm(prev => ({ ...prev, prefDosham: v, prefDoshamType: v === 'Yes' ? prev.prefDoshamType : '' }))}
                                        options={[ANY_PREFERENCE, 'No', 'Yes', "Don't Know"]} />
                                </Field>
                                {form.prefDosham === 'Yes' && (
                                    <Field label="Preferred Dosham Type"><Select ariaLabel="Preferred Dosham type" value={form.prefDoshamType} onChange={v => set('prefDoshamType', v)} options={doshamTypeOptions} placeholder="Select Dosham type" /></Field>
                                )}
                            </div>
                        </section>

                        <section className="profile-edit-section profile-edit-section--wide">
                            <SectionHeader icon={Leaf} title="Lifestyle preferences" subtitle="Eating, drinking and smoking preferences for the partner" />
                            <div className="profile-edit-fields profile-edit-fields--lifestyle">
                                <Field label="Eating Habits"><Select ariaLabel="Preferred eating habits" value={form.prefEatingHabits} onChange={v => set('prefEatingHabits', v)} options={LIFESTYLE_OPTIONS.eating} /></Field>
                                <Field label="Drinking Habits"><Select ariaLabel="Preferred drinking habits" value={form.prefDrinkingHabits} onChange={v => set('prefDrinkingHabits', v)} options={LIFESTYLE_OPTIONS.drinking} /></Field>
                                <Field label="Smoking Habits"><Select ariaLabel="Preferred smoking habits" value={form.prefSmokingHabits} onChange={v => set('prefSmokingHabits', v)} options={LIFESTYLE_OPTIONS.smoking} /></Field>
                            </div>
                        </section>

                        <Section icon={HeartHandshake} title="Expectation details" subtitle="How soon the member wants to marry and what they expect">
                            <Field label="Marriage Urgency">
                                <Select ariaLabel="Marriage urgency" value={form.marriageUrgency} onChange={v => set('marriageUrgency', v)} options={[...urgencyOptions, ...(form.marriageUrgency && !urgencyOptions.includes(form.marriageUrgency) ? [form.marriageUrgency] : [])]} />
                            </Field>
                            <Field label="Expectation Summary" hint="up to 500 characters">
                                <textarea name="partnerExpectation" rows={3} maxLength={500} value={form.partnerExpectation} onChange={onChange} />
                            </Field>
                        </Section>

                        <section className="profile-edit-section profile-edit-section--wide">
                            <SectionHeader icon={FileText} title="About this member" subtitle="A short introduction visible on the matrimony profile" />
                            <div className="profile-edit-section__body">
                            <textarea
                                name="about" value={form.about} onChange={onChange} maxLength={500}
                                placeholder="A short note about the member"
                            />
                            </div>
                        </section>
                    </div>

                    <div className="profile-edit-actions">
                        <div><strong>Ready to update this profile?</strong><span>Changes will be visible across the matrimony platform.</span></div>
                        <button type="button" className="profile-edit-cancel" onClick={() => navigate(`/profiles/${id}`)}>Cancel</button>
                        <button type="submit" className="profile-edit-save" disabled={saving}>
                            <Save size={15} /> {saving ? 'Saving…' : 'Save changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
    <header className="profile-edit-section__header"><span><Icon size={17} /></span><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div></header>
);

const Section = ({ icon, title, subtitle, children }) => (
    <section className="profile-edit-section">
        <SectionHeader icon={icon} title={title} subtitle={subtitle} />
        <div className="profile-edit-fields">{children}</div>
    </section>
);

const Field = ({ label, hint, children }) => (
    <div className="profile-edit-field">
        <label>{label}{hint && <span> · {hint}</span>}</label>
        {children}
    </div>
);

export default ProfileEdit;
